import { useState, useEffect, useMemo } from "react";
import { X, ChevronDown, Check, Minus } from "lucide-react";

const BUCKET_STYLE = {
  Tasks: {
    color: "#378ADD",
    soft: "rgba(55,138,221,0.18)",
    text: "#85B7EB",
    border: "rgba(55,138,221,0.3)",
  },
  Skills: {
    color: "#639922",
    soft: "rgba(99,153,34,0.18)",
    text: "#97C459",
    border: "rgba(99,153,34,0.3)",
  },
  Abilities: {
    color: "#7F77DD",
    soft: "rgba(127,119,221,0.18)",
    text: "#AFA9EC",
    border: "rgba(127,119,221,0.3)",
  },
  Knowledge: {
    color: "#BA7517",
    soft: "rgba(186,117,23,0.18)",
    text: "#EF9F27",
    border: "rgba(186,117,23,0.3)",
  },
  Other: {
    color: "#888888",
    soft: "rgba(128,128,128,0.18)",
    text: "#AAAAAA",
    border: "rgba(128,128,128,0.3)",
  },
};

function pctColor(pct) {
  if (pct >= 100) return "var(--color-primary)";
  if (pct <= 0) return "#E24B4A";
  return "#EF9F27";
}

// ─── Donut summarizing weighted coverage across all buckets ────────────────
function CoverageDonut({ buckets, totalEarned, totalAll }) {
  const RADIUS = 42;
  const CIRC = 2 * Math.PI * RADIUS;
  const overallPct = totalAll > 0 ? (totalEarned / totalAll) * 100 : 0;

  // Each bucket gets an arc sized by its share of all TKSAs. Within the arc,
  // the "earned" portion is full opacity, the remainder soft.
  let cursor = 0;
  const segments = [];
  buckets.forEach((b) => {
    const style = BUCKET_STYLE[b.label ?? b.key] ?? BUCKET_STYLE.Other;
    const bucketFrac = totalAll > 0 ? b.total / totalAll : 0;
    const bucketLen = bucketFrac * CIRC;
    const earnedFrac = b.total > 0 ? b.earned / b.total : 0;
    const earnedLen = bucketLen * earnedFrac;
    const remainingLen = bucketLen - earnedLen;

    if (earnedLen > 0.01) {
      segments.push({
        color: style.color,
        opacity: 1,
        dashArray: `${earnedLen} ${CIRC - earnedLen}`,
        dashOffset: -cursor,
      });
      cursor += earnedLen;
    }
    if (remainingLen > 0.01) {
      segments.push({
        color: style.color,
        opacity: 0.22,
        dashArray: `${remainingLen} ${CIRC - remainingLen}`,
        dashOffset: -cursor,
      });
      cursor += remainingLen;
    }
  });

  return (
    <div className="relative flex items-center justify-center shrink-0 mx-auto sm:mx-0">
      <svg
        width="132"
        height="132"
        viewBox="0 0 100 100"
        role="img"
        aria-label={`TKSA score: ${totalEarned} of ${totalAll} earned`}
      >
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          stroke="var(--color-border-light)"
          strokeOpacity="0.25"
          strokeWidth="9"
        />
        <g transform="rotate(-90 50 50)">
          {segments.map((seg, i) => (
            <circle
              key={i}
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke={seg.color}
              strokeOpacity={seg.opacity}
              strokeWidth="9"
              strokeLinecap="butt"
              strokeDasharray={seg.dashArray}
              strokeDashoffset={seg.dashOffset}
            />
          ))}
        </g>
        <text
          x="50"
          y="46"
          textAnchor="middle"
          style={{
            fill: "var(--color-primary)",
            fontSize: "18px",
            fontWeight: 700,
            fontFamily: "'Courier New', monospace",
          }}
        >
          {totalEarned}/{totalAll}
        </text>
        <text
          x="50"
          y="62"
          textAnchor="middle"
          style={{
            fill: "var(--color-text-light-green)",
            fontSize: "8px",
            fontWeight: 600,
            letterSpacing: "0.1em",
          }}
        >
          {Math.round(overallPct)}% TKSAs
        </text>
      </svg>
    </div>
  );
}

// ─── Status chip for a single question within a TKSA row ───────────────────
function QuestionChip({ q }) {
  const correct = q.status === "correct";
  const pending = q.status === "pending";
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md"
      style={{
        background: correct
          ? "rgba(0,255,0,0.08)"
          : pending
            ? "var(--color-border-light)"
            : "rgba(226,75,74,0.12)",
        color: correct
          ? "var(--color-primary)"
          : pending
            ? "var(--color-text-muted)"
            : "#E24B4A",
        border: `1px solid ${
          correct
            ? "var(--color-border-medium)"
            : pending
              ? "var(--color-border-light)"
              : "rgba(226,75,74,0.3)"
        }`,
      }}
      title={
        correct
          ? "Answered correctly"
          : pending
            ? "Not resolved"
            : "Answered incorrectly / attempts exhausted"
      }
    >
      Q{q.index + 1}
      {correct ? (
        <Check className="h-3 w-3" />
      ) : pending ? (
        <Minus className="h-3 w-3" />
      ) : (
        <X className="h-3 w-3" />
      )}
    </span>
  );
}

// ─── A single expandable TKSA row showing its per-question results ─────────
function TksaRow({ entry }) {
  const [open, setOpen] = useState(false);
  const style = BUCKET_STYLE[entry.bucket] ?? BUCKET_STYLE.Other;
  const pct =
    entry.attemptsTotal > 0
      ? Math.round((entry.attemptsCorrect / entry.attemptsTotal) * 100)
      : 0;
  const fill = pctColor(pct);

  return (
    <div
      className="rounded-md overflow-hidden"
      style={{
        border: "1px solid var(--color-border-light)",
        background: "var(--color-card)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-(--color-primary)/5 transition-colors"
      >
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: style.color }}
        />
        <span
          className="text-xs font-mono font-semibold shrink-0 w-14.5"
          style={{ color: "var(--color-text-primary)" }}
        >
          {entry.id}
        </span>
        <div
          className="flex-1 h-1.5 rounded-full overflow-hidden min-w-12.5"
          style={{ background: style.soft }}
        >
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: fill }}
          />
        </div>
        <span
          className="text-[11px] font-mono tabular-nums shrink-0 w-16 text-right"
          style={{ color: "var(--color-text-muted)" }}
        >
          {entry.attemptsCorrect}/{entry.attemptsTotal} · {pct}%
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-(--color-text-muted) transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          className="px-3 pb-3 pt-1 border-t"
          style={{ borderColor: "var(--color-border-light)" }}
        >
          <p className="text-[10.5px] uppercase tracking-wide text-(--color-text-muted) mb-2 mt-1">
            Questions assessing this {entry.bucketSingular}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {entry.questions.map((q) => (
              <QuestionChip key={q.index} q={q} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TksaCoverageModal({
  open,
  onClose,
  buckets = [],
  totalEarned = 0,
  totalAll = 0,
  tksaEntries = [],
  questionsCorrect = 0,
  questionsResolved = 0,
  totalQuestions = 0,
}) {
  // Close on Escape + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Group entries by bucket for the detail list, preserving bucket order.
  const grouped = useMemo(() => {
    const order = ["Tasks", "Skills", "Abilities", "Knowledge", "Other"];
    const map = {};
    tksaEntries.forEach((e) => {
      (map[e.bucket] ||= []).push(e);
    });
    return order
      .filter((k) => map[k]?.length)
      .map((k) => ({
        label: k,
        items: map[k].sort((a, b) => a.id.localeCompare(b.id)),
      }));
  }, [tksaEntries]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="TKSA coverage breakdown"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-(--color-bg)/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* panel */}
      <div
        className="relative w-full max-w-4xl max-h-[88vh] flex flex-col rounded-md overflow-hidden"
        style={{
          border: "1px solid var(--color-border-medium)",
          boxShadow: "0 20px 60px -10px rgba(0,0,0,0.6)",
        }}
      >
        {/* top edge glow */}
        <div
          className="absolute top-0 left-0 right-0 h-px pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, var(--color-border-medium) 20%, var(--color-border-dark) 50%, var(--color-border-medium) 80%, transparent 100%)",
          }}
        />

        {/* header */}
        <div
          className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--color-border-light)" }}
        >
          <div className="flex flex-col gap-0.5">
            <h2
              className="text-sm font-bold uppercase tracking-[0.12em]"
              style={{ color: "var(--color-primary)" }}
            >
              TKSA Coverage
            </h2>
            <p
              className="text-xs"
              style={{ color: "var(--color-text-light-green)" }}
            >
              How your answers map to assessed competencies
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md flex items-center justify-center text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-primary)/10 transition-colors"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        {/* scrollable body */}
        <div className="overflow-y-auto px-5 py-5 space-y-5">
          {/* stat strip — keeps the three numbers separate so a missed
              question can't hide behind full TKSA coverage */}
          <div className="grid grid-cols-3 gap-2.5">
            {[
              {
                label: "Questions correct",
                value: `${questionsCorrect}/${totalQuestions}`,
              },
              {
                label: "Questions resolved",
                value: `${questionsResolved}/${totalQuestions}`,
              },
              {
                label: "TKSAs earned",
                value: `${totalEarned}/${totalAll}`,
              },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-md px-3 py-2.5"
                style={{
                  background: "var(--color-primary-50)",
                  border: "1px solid var(--color-border-light)",
                }}
              >
                <div className="text-[10px] uppercase tracking-wide text-(--color-text-muted) mb-1">
                  {s.label}
                </div>
                <div
                  className="text-lg font-mono font-bold tabular-nums"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  {s.value}
                </div>
              </div>
            ))}
          </div>

          {/* donut + per-category bars */}
          <div
            className="rounded-md px-4 py-4 grid gap-5 sm:grid-cols-[auto_1fr] items-center"
            style={{ border: "1px solid var(--color-border-light)" }}
          >
            <CoverageDonut
              buckets={buckets}
              totalEarned={totalEarned}
              totalAll={totalAll}
            />
            <div className="flex flex-col gap-2.5 min-w-0">
              <div
                className="text-[10.5px] font-bold uppercase tracking-[0.14em] mb-0.5"
                style={{ color: "var(--color-primary)" }}
              >
                By category
              </div>
              {buckets.map((b) => {
                const label = b.label ?? b.key;
                const style = BUCKET_STYLE[label] ?? BUCKET_STYLE.Other;
                const pct = b.total > 0 ? (b.earned / b.total) * 100 : 0;
                return (
                  <div
                    key={label}
                    className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5"
                    style={{
                      background: style.soft,
                      border: `1px solid ${style.border}`,
                    }}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: style.color }}
                    />
                    <span
                      className="text-xs font-semibold shrink-0 w-18"
                      style={{ color: style.color }}
                    >
                      {label}
                    </span>
                    <div
                      className="flex-1 h-2 rounded-full overflow-hidden min-w-15"
                      style={{ background: "rgba(128,128,128,0.2)" }}
                    >
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: style.color }}
                      />
                    </div>
                    <span
                      className="text-[11px] font-mono font-semibold tabular-nums shrink-0 w-14.5 text-right"
                      style={{ color: style.color }}
                    >
                      {b.earned}/{b.total} · {Math.round(pct)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* per-TKSA detail — the part that surfaces shared-tag questions */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div
                className="text-[10.5px] font-bold uppercase tracking-[0.14em]"
                style={{ color: "var(--color-primary)" }}
              >
                Per-TKSA breakdown
              </div>
              <span className="text-[10.5px] text-(--color-text-muted)">
                tap a row to see its questions
              </span>
            </div>

            {grouped.map((group) => {
              const style = BUCKET_STYLE[group.label] ?? BUCKET_STYLE.Other;
              return (
                <div key={group.label} className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: style.color }}
                    />
                    <span
                      className="text-[11px] font-semibold uppercase tracking-wide"
                      style={{ color: style.text }}
                    >
                      {group.label}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {group.items.map((entry) => (
                      <TksaRow key={entry.id} entry={entry} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* legend */}
          <div
            className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1 text-[11px]"
            style={{ color: "var(--color-text-muted)" }}
          >
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3 w-3" style={{ color: "var(--color-primary)" }} />
              correct
            </span>
            <span className="inline-flex items-center gap-1.5">
              <X className="h-3 w-3" style={{ color: "#E24B4A" }} />
              incorrect / exhausted
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Minus className="h-3 w-3" />
              unresolved
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}